import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  ArrowSmRightIcon,
  BadgeCheckIcon,
  CashIcon,
  ChatIcon,
  CheckCircleIcon,
  CheckIcon,
  CogIcon,
  ExclamationCircleIcon,
  UserIcon,
  XIcon,
} from "@heroicons/react/outline";
import { Briefcase, RefreshCcw } from "lucide-react";
import DeleteObjectModal from "src/components/DeleteObjectModal";
import ErrorBanner from "src/components/ErrorBanner";
import HeaderNavigation from "src/components/HeaderNavigation";
import Tabs from "src/components/Tabs";
import { useChannel } from "src/hooks/useChannel";
import useChannelProfile from "src/hooks/useChannelProfile";
import useHilosStore from "src/hooks/useHilosStore";
import { getTranslationPayload as t } from "src/i18n";
import { buildRoute } from "src/router/router";
import ChannelCloudAPIUpdate from "./ChannelCloudAPIUpdate";
import ChannelQualityNotices from "./ChannelQualityNotices";
import ChannelStatusBadge from "./ChannelStatusBadge";
import ChannelWhatsAppTutorialCards from "./ChannelWhatsAppTutorialCards";
import * as meta from "./Meta";
import {
  HEALTH_STATUS,
  PROFILE_MESSAGING_TIERS,
  PROFILE_VERTICALS,
} from "./Meta";
import ProfileEditForm from "./ProfileEditForm";

export const CLOUD_API_LINKS = [
  {
    title: t(
      "channel:whatsapp-links.payment-settings",
      "Go to WA Payment Settings"
    ),
    href: "https://business.facebook.com/billing_hub/payment_settings",
    icon: (
      <CashIcon className="h-5 w-5 text-gray-500 mr-2" aria-hidden="true" />
    ),
  },
  {
    title: t(
      "channel:whatsapp-links.whatsapp-manager",
      "Go to WhatsApp Manager"
    ),
    href: "https://business.facebook.com/latest/whatsapp_manager/phone_numbers",
    icon: (
      <Briefcase className="h-5 w-5 text-gray-500 mr-2" aria-hidden="true" />
    ),
  },
  {
    title: t(
      "channel:whatsapp-links.business-verification",
      "Business Verification"
    ),
    href: "https://business.facebook.com/settings/security",
    icon: (
      <CheckCircleIcon
        className="h-5 w-5 text-gray-500 mr-2"
        aria-hidden="true"
      />
    ),
  },
];

export default function ChannelCloudAPIDetail() {
  const { t } = useTranslation();
  const params = useParams();
  const channelId = params.id as string;
  const [showDeactivateChannelModal, setShowDeactivateChannelModal] =
    useState(false);
  const { channel, updateChannel } = useChannel(channelId);
  const [deactivateStatus, setDeactivateStatus] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const { session } = useHilosStore();
  const {
    profileData,
    infoData,
    isLoading,
    isProfileError,
    profileError,
    isInfoError,
    infoError,
    updateProfile,
    isUpdating,
    isUpdateSuccess,
    resetUpdateStatus,
    registerPhone,
    isRegistering,
  } = useChannelProfile(channelId);

  const channelInfo = useMemo(() => {
    return meta.AVAILABLE_CHANNELS.find(
      (chInfo) => chInfo.provider === channel?.channel_provider
    );
  }, [channel]);

  const handleDisableChannel = useCallback(() => {
    setDeactivating(true);
    updateChannel({
      status: "INACTIVE",
    });
    setTimeout(() => {
      setShowDeactivateChannelModal(false);
      setDeactivating(false);
    }, 2000);
  }, [updateChannel]);

  const handleEnableChannel = useCallback(() => {
    updateChannel({
      status: "ACTIVE",
    });
  }, [updateChannel]);

  useEffect(() => {
    if (channel?.status === "INACTIVE") {
      setDeactivateStatus(true);
    }
    if (channel?.status === "ACTIVE") {
      setDeactivateStatus(false);
    }
  }, [channel, deactivateStatus]);

  if (!channel || !channelInfo) {
    return null;
  }

  return (
    <div className="h-screen overflow-y-auto bg-gray-50">
      <div className="py-4 px-4 sm:border-b sm:border-gray-200 sm:px-6 lg:px-8">
        <HeaderNavigation
          navPages={[
            {
              name: "Home",
              url: buildRoute("dashboard"),
            },
            {
              name: "Settings",
              url: buildRoute("config-account"),
            },
            {
              name: "WhatsApp Accounts",
              url: buildRoute("config-channel"),
            },
            {
              name: channel?.name,
              url: buildRoute("config-channel-cloud-api-details", {
                id: channelId,
              }),
            },
          ]}
        />
        <div className="lg:flex lg:items-center lg:justify-between">
          <div className="min-w-0 flex-1">
            <h2 className="mt-2 flex items-center text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl">
              <ChatIcon className="mr-2 h-7 w-7" aria-hidden="true" />
              {t("channel:update-channel-title", "Channel {{channel}}", {
                channel: channel.name,
              })}
            </h2>
          </div>
        </div>
      </div>

      <div className="mt-4 ml-2">
        <div className="py-4 px-4 sm:px-6 lg:px-8">
          <p className="mt-2 ml-2 flex items-center text-xl font-bold leading-7 text-gray-900 sm:truncate">
            {t("channel:channel-details.info-title", "About your channel")}
          </p>
        </div>

        <div className="mx-10 grid grid-cols-1 sm:grid-cols-3 lg:gap-6 items-start">
          <div className="flex flex-col divide-y divide-gray-200 rounded-lg bg-white text-center shadow">
            <div className="flex flex-1 flex-col items-center p-6">
              <div className="text-xs font-medium uppercase tracking-wider text-gray-500">
                <span>{channel.name}</span>
              </div>
              <div className="flex flex-row gap-3">
                <span
                  className={`flex mt-3 items-center rounded-full bg-${channel.channel_color}-100 px-2 py-1 text-xs font-medium text-${channel.channel_color}-800`}
                >
                  <channelInfo.icon className="mr-1 text-lg" />
                  {channelInfo.type}
                </span>
                {channel.id === session?.account.default_channel && (
                  <span className="flex mt-3 items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                    <CheckCircleIcon className="-ml-0.5 mr-1 h-5 aspect-square" />
                    {t("channel:default-channel", "Default channel")}
                  </span>
                )}
              </div>
              <div className="flex flex-1 flex-col text-left space-y-3">
                {channel.status === "INACTIVE" && channel.connection_error_msg && (
                  <div className="rounded-md bg-yellow-50 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <ExclamationCircleIcon
                          className="h-5 w-5 text-yellow-400"
                          aria-hidden="true"
                        />
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">
                          {t(
                            "channel:connection-failed",
                            "Channel connection failed"
                          )}
                        </h3>
                        <div className="mt-2 text-sm text-yellow-700">
                          <p>{channel.connection_error_msg}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {isInfoError && (
                <ErrorBanner
                  message={
                    infoError ||
                    t(
                      "channel:info.error-loading",
                      "Error loading phone information"
                    )
                  }
                />
              )}

              {channel.status !== "INACTIVE" && !isInfoError && infoData && (
                <>
                  {isProfileError ? (
                    <ErrorBanner
                      message={
                        profileError ||
                        t(
                          "channel:profile.error-loading",
                          "Error loading profile information"
                        )
                      }
                    />
                  ) : (
                    <>
                      <img
                        src={
                          profileData?.profile_picture_url ||
                          "https://hilos-media.s3.us-east-2.amazonaws.com/public-media/default+profile+picture.png"
                        }
                        alt="WhatsApp Profile"
                        className="mt-3 aspect-square rounded-full object-cover w-24 h-24"
                      />
                      <div className="flex flex-col items-center justify-between mt-3">
                        <div className="flex flex-row items-center">
                          <p className="font-bold text-xl">
                            {infoData.verified_name}
                          </p>
                          {infoData.is_official_business_account && (
                            <img
                              src="https://hilos-media.s3.us-east-2.amazonaws.com/public-media/verified.png"
                              alt="Verified account"
                              className="h-4 w-4"
                            />
                          )}
                        </div>
                        {infoData.new_name_status &&
                          ["APPROVED", "AVAILABLE_WITHOUT_REVIEW"].includes(
                            infoData.new_name_status.toString()
                          ) && (
                            <div className="flex flex-col justify-center items-center gap-1 text-sm p-2 bg-blue-50 rounded-md text-blue-800">
                              <p className="">
                                {t(
                                  "channel.update-name",
                                  "A recent name change request has been approved."
                                )}
                              </p>
                              <button
                                className="flex justify-center rounded-md items-center p-2 border gap-2 border-solid text-xs border-blue-200 border-1"
                                onClick={(e) => {
                                  e.preventDefault();
                                  registerPhone();
                                }}
                                disabled={isRegistering}
                                title={t(
                                  "channel:update-name.button",
                                  "Apply name change now"
                                )}
                              >
                                {t(
                                  "channel:update-name.button",
                                  "Apply name change now"
                                )}
                                <RefreshCcw
                                  className={`w-4 h-4 ${
                                    isRegistering ? "animate-spin" : ""
                                  }`}
                                />
                              </button>
                            </div>
                          )}
                      </div>
                    </>
                  )}

                  <p>{infoData?.display_phone_number}</p>

                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div className="flex flex-col justify-start items-center bg-slate-100 px-4 py-2 rounded-md">
                      <p className="text-slate-600 text-sm">Hilos Status</p>
                      <ChannelStatusBadge status={channel.status} />
                    </div>
                    <div className="flex flex-col justify-start items-center bg-slate-100 px-4 py-2 rounded-md">
                      <p className="text-slate-600 text-sm">Meta Status</p>
                      <div className="w-fit items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                        {infoData?.status &&
                        meta.META_CHANNEL_STATUS[infoData?.status]
                          ? t(meta.META_CHANNEL_STATUS[infoData?.status]?.label)
                          : t("unknown")}
                      </div>
                    </div>
                    <div className="flex flex-col justify-start items-center bg-slate-100 px-4 py-2 rounded-md">
                      <p className="text-slate-600 text-sm">Channel Quality</p>
                      <div className="w-fit items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                        {infoData?.quality_score?.score &&
                        HEALTH_STATUS[infoData?.quality_score?.score]
                          ? t(
                              HEALTH_STATUS[infoData?.quality_score?.score]
                                ?.label
                            )
                          : t("unknown")}
                      </div>
                    </div>
                    <div className="flex flex-col justify-start items-center bg-slate-100 px-4 py-2 rounded-md">
                      <p className="text-slate-600 text-sm">
                        {t(
                          "channel.channel-details.messaging-tier.title",
                          "Messaging tier"
                        )}
                      </p>
                      <p className="text-slate-600 font-bold text-xs">
                        {infoData?.messaging_limit_tier &&
                        PROFILE_MESSAGING_TIERS[infoData?.messaging_limit_tier]
                          ? t(
                              PROFILE_MESSAGING_TIERS[
                                infoData?.messaging_limit_tier
                              ]?.label
                            )
                          : t("unknown")}
                      </p>
                    </div>

                    {!isProfileError && profileData && (
                      <div className="col-span-2 divide-y-3 divide-slate-400 bg-slate-100 rounded-md grid grid-cols-1 text-sm text-left text-slate-600 py-2 px-4">
                        <div>
                          {profileData.vertical &&
                          PROFILE_VERTICALS[profileData.vertical]
                            ? t(PROFILE_VERTICALS[profileData.vertical].label)
                            : t("unknown")}
                        </div>
                        <div>{profileData?.description}</div>
                        <div>{profileData?.about}</div>
                        <div>{profileData?.address}</div>
                        <div className="text-green-600">
                          {profileData?.email}
                        </div>
                        {profileData?.websites?.map((website) => (
                          <div key={website} className="text-green-600">
                            {website}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            {CLOUD_API_LINKS.map((cLink) => (
              <a
                href={cLink.href}
                key={cLink.href}
                target="_blank"
                rel="noreferrer"
                className="focus:outline-none"
              >
                <div className="group relative order-last divide-gray-200 p-3 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500 sm:col-span-3 md:col-span-3 lg:order-none lg:col-span-2 last:rounded-b-lg flex items-center  justify-between ">
                  <h3 className="text-sm text-left flex items-center">
                    {cLink.icon}
                    {t(cLink.title)}
                  </h3>
                  <ArrowSmRightIcon className="h-5 w-5 text-gray-400" />
                </div>
              </a>
            ))}

            {channel.status === "ACTIVE" && (
              <button
                type="button"
                onClick={() => setShowDeactivateChannelModal(true)}
                className="group relative order-last divide-gray-200 p-3 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500 sm:col-span-3 md:col-span-3 lg:order-none lg:col-span-2 last:rounded-b-lg flex items-center justify-between hover:bg-red-50"
              >
                <h3 className="text-sm text-left flex items-center text-red-700">
                  <XIcon
                    className="h-5 w-5 text-red-500 mr-2"
                    aria-hidden="true"
                  />
                  {t(
                    "channel:channel-details.disable-button",
                    "Deactivate channel"
                  )}
                </h3>
              </button>
            )}

            {channel.status === "INACTIVE" && (
              <button
                type="button"
                onClick={handleEnableChannel}
                className="group relative order-last divide-gray-200 p-3 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500 sm:col-span-3 md:col-span-3 lg:order-none lg:col-span-2 last:rounded-b-lg flex items-center justify-between hover:bg-green-50"
              >
                <h3 className="text-sm text-left flex items-center text-green-700">
                  <CheckIcon
                    className="h-5 w-5 text-green-500 mr-2"
                    aria-hidden="true"
                  />
                  {t(
                    "channel:channel-details.enable-button",
                    "Activate channel"
                  )}
                </h3>
              </button>
            )}
          </div>

          <div className="col-span-2 flex flex-col divide-y divide-gray-200 rounded-lg bg-white text-center shadow">
            <div>
              <Tabs
                tabs={[
                  {
                    label: t(
                      "channel:channel-details.tabs.profile",
                      "WhatsApp Profile"
                    ),
                    icon: UserIcon,
                  },
                  {
                    label: t(
                      "channel:channel-details.tabs.settings",
                      "Channel Settings"
                    ),
                    icon: CogIcon,
                  },
                  {
                    label: t(
                      "channel:channel-details.tabs.quality",
                      "Channel Quality"
                    ),
                    icon: BadgeCheckIcon,
                  },
                ]}
              >
                {isLoading ? (
                  <div className="text-center py-8">
                    <p>{t("channel:loading", "Loading...")}</p>
                  </div>
                ) : isProfileError ? (
                  <ErrorBanner
                    message={
                      profileError ||
                      t(
                        "channel:profile.error-loading",
                        "Error loading profile information"
                      )
                    }
                  />
                ) : (
                  !channel.is_sandbox &&
                  channel.status !== "INACTIVE" &&
                  profileData && (
                    <ProfileEditForm
                      profile={profileData}
                      onSubmit={(updatedProfile) => {
                        updateProfile(updatedProfile);
                        setTimeout(() => {
                          resetUpdateStatus();
                        }, 5000);
                      }}
                      isSubmitting={isUpdating}
                      isSuccess={isUpdateSuccess}
                    />
                  )
                )}

                <ChannelCloudAPIUpdate channelId={channelId} />

                <ChannelQualityNotices
                  healthStatus={infoData?.health_status}
                  isError={isInfoError}
                  errorMessage={
                    infoError ||
                    t(
                      "channel:info.error-loading",
                      "Error loading phone information"
                    )
                  }
                />
              </Tabs>
            </div>
          </div>
        </div>

        <ChannelWhatsAppTutorialCards />
      </div>
      <DeleteObjectModal
        obj={channel}
        objDescription={channel.name as string}
        actionDescription={t(
          "channel:delete-channel",
          "Deactivating this channel will make it stop receiving messages."
        )}
        onDelete={handleDisableChannel}
        deleteSuccess={deactivateStatus}
        deleteSubmitted={deactivating}
        show={showDeactivateChannelModal}
        setShow={setShowDeactivateChannelModal}
        title={t(
          "channel:deactivate-channel-title",
          "Are you sure you want to deactivate this channel?"
        )}
        deleteButtonText={t(
          "channel:deactivate-channel-button",
          "Yes, deactivate"
        )}
        deleteButtonSuccessText={t(
          "channel:deactivate-channel-success",
          "Deactivated"
        )}
        deleteButtonSubmittingText={t(
          "channel:deactivate-channel-submitting",
          "Deactivating..."
        )}
      />
    </div>
  );
}
